import { createEffect, createMemo, onCleanup } from "solid-js"
import { createStore, produce } from "solid-js/store"
import { createSimpleContext } from "@openanalyst-ai/ui/context"
import type { PermissionRequest, PermissionRuleset } from "@openanalyst-ai/sdk/v2/client"
import { Persist, persisted } from "@/utils/persist"
import { useGlobalSDK } from "@/context/global-sdk"
import { useGlobalSync } from "./global-sync"
import { useParams } from "@solidjs/router"
import { decode64 } from "@/utils/base64"
import { usePlatform } from "@/context/platform"
import { bounceDock } from "@/utils/dock-bounce"
import {
  acceptKey,
  directoryAcceptKey,
  directoryPermissionMode as getDirectoryPermissionMode,
  NEVER_LEARN_PERMISSIONS,
  permissionModeAutoAccepts,
  permissionModeAutoAcceptsRequest,
  permissionModeStoreUpdate,
  type PermissionMode,
  sessionPermissionMode,
} from "./permission-auto-respond"

type PermissionRespondFn = (input: {
  sessionID: string
  permissionID: string
  response: "once" | "always" | "reject"
  directory?: string
}) => void

function isNonAllowRule(rule: unknown) {
  if (!rule) return false
  if (typeof rule === "string") return rule !== "allow"
  if (typeof rule !== "object") return false
  if (Array.isArray(rule)) return false

  for (const action of Object.values(rule)) {
    if (action !== "allow") return true
  }

  return false
}

function hasPermissionPromptRules(permission: unknown) {
  if (!permission) return false
  if (typeof permission === "string") return permission !== "allow"
  if (typeof permission !== "object") return false
  if (Array.isArray(permission)) return false

  const config = permission as Record<string, unknown>
  return Object.values(config).some(isNonAllowRule)
}

// The permission mode is conveyed to the engine purely as a per-session ruleset
// written into `session.permission`. The engine merges it as the LOWEST-priority
// layer (Permission.mergeSessionMode) so it only governs tools no explicit
// agent/user rule already covers. The three modes map to:
//   • "default"     (Ask)    → ask for everything (engine prompts; approvals are
//                              remembered via the learned-rules cache on "always").
//   • "auto-review" (Auto)   → allow everything EXCEPT bash, which still asks.
//                              (Agent safety rails — external_directory, .env reads,
//                              doom_loop — still apply since they sit after this.)
//   • "full-access" (Bypass) → allow everything, zero prompts.
// A NON-EMPTY ruleset is what tells the engine a mode is active, so even "default"
// must emit its `*: ask` rule (empty would fall through to the agent's default
// allow-all and silently skip prompting).
function sessionPermissionRulesForMode(mode: PermissionMode): PermissionRuleset {
  if (mode === "full-access") return [{ permission: "*", pattern: "*", action: "allow" }]
  if (mode === "auto-review")
    return [
      { permission: "*", pattern: "*", action: "allow" },
      { permission: "bash", pattern: "*", action: "ask" },
    ]
  // "default" → Ask mode
  return [{ permission: "*", pattern: "*", action: "ask" }]
}

export const { use: usePermission, provider: PermissionProvider } = createSimpleContext({
  name: "Permission",
  init: () => {
    const params = useParams()
    const globalSDK = useGlobalSDK()
    const globalSync = useGlobalSync()
    const platform = usePlatform()

    const permissionsEnabled = createMemo(() => {
      const directory = decode64(params.dir)
      if (!directory) return false
      const [store] = globalSync.child(directory)
      return hasPermissionPromptRules(store.config.permission)
    })

    const [store, setStore, _, ready] = persisted(
      {
        ...Persist.global("permission", ["permission.v3"]),
        migrate(value) {
          if (!value || typeof value !== "object" || Array.isArray(value)) return value

          const data = value as Record<string, unknown>
          if (data.autoAccept) return value

          return {
            ...data,
            autoAccept:
              typeof data.autoAcceptEdits === "object" && data.autoAcceptEdits && !Array.isArray(data.autoAcceptEdits)
                ? data.autoAcceptEdits
                : {},
            mode: {},
          }
        },
      },
      createStore({
        autoAccept: {} as Record<string, boolean>,
        mode: {} as Record<string, PermissionMode>,
      }),
    )

    // When config has permission: "allow", auto-enable directory-level auto-accept
    createEffect(() => {
      if (!ready()) return
      const directory = decode64(params.dir)
      if (!directory) return
      const [childStore] = globalSync.child(directory)
      const perm = childStore.config.permission
      if (typeof perm === "string" && perm === "allow") {
        const key = directoryAcceptKey(directory)
        if (store.autoAccept[key] === undefined && store.mode[key] === undefined) {
          setStore(
            produce((draft) => {
              draft.autoAccept[key] = true
              draft.mode[key] = "full-access"
            }),
          )
        }
      }
    })

    const MAX_RESPONDED = 1000
    const RESPONDED_TTL_MS = 60 * 60 * 1000
    const responded = new Map<string, number>()
    const enableVersion = new Map<string, number>()

    function pruneResponded(now: number) {
      for (const [id, ts] of responded) {
        if (now - ts < RESPONDED_TTL_MS) break
        responded.delete(id)
      }

      for (const id of responded.keys()) {
        if (responded.size <= MAX_RESPONDED) break
        responded.delete(id)
      }
    }

    const respond: PermissionRespondFn = (input) => {
      globalSDK.client.permission.respond(input).catch(() => {
        responded.delete(input.permissionID)
      })
    }

    // Auto-respond to a permission ask based on the effective mode. LEARNED
    // PERMISSIONS: under full access the response is "always", not "once" —
    // the engine records the tool into the project's persisted approved
    // ruleset, so the tools a session ACTUALLY USES stop re-asking once full
    // access is later turned off (or the app restarts). Auto-review keeps
    // "once": it is a per-action review mode and must not create permanent
    // allows.
    function respondOnce(permission: PermissionRequest, directory?: string) {
      const session = directory ? globalSync.child(directory, { bootstrap: false })[0].session : []
      const mode = sessionPermissionMode(store.mode, store.autoAccept, session, permission.sessionID, directory)
      const now = Date.now()
      const hit = responded.has(permission.id)
      responded.delete(permission.id)
      responded.set(permission.id, now)
      pruneResponded(now)
      if (hit) return
      const learn = mode === "full-access" && !permission.force && !NEVER_LEARN_PERMISSIONS.has(permission.permission)
      respond({
        sessionID: permission.sessionID,
        permissionID: permission.id,
        response: learn ? "always" : "once",
        directory,
      })
    }

    function isAutoAccepting(sessionID: string, directory?: string) {
      const session = directory ? globalSync.child(directory, { bootstrap: false })[0].session : []
      return permissionModeAutoAccepts(
        sessionPermissionMode(store.mode, store.autoAccept, session, sessionID, directory),
      )
    }

    function isAutoAcceptingDirectory(directory: string) {
      return permissionModeAutoAccepts(getDirectoryPermissionMode(store.mode, store.autoAccept, directory))
    }

    function shouldAutoRespond(permission: PermissionRequest, directory?: string) {
      const session = directory ? globalSync.child(directory, { bootstrap: false })[0].session : []
      const mode = sessionPermissionMode(store.mode, store.autoAccept, session, permission.sessionID, directory)
      return permissionModeAutoAcceptsRequest(mode, permission.permission, permission.force)
    }

    function bumpEnableVersion(sessionID: string, directory?: string) {
      const key = acceptKey(sessionID, directory)
      const next = (enableVersion.get(key) ?? 0) + 1
      enableVersion.set(key, next)
      return next
    }

    const unsubscribe = globalSDK.event.listen((e) => {
      const event = e.details
      if (event?.type !== "permission.asked") return

      const perm = event.properties
      if (!shouldAutoRespond(perm, e.name)) {
        bounceDock(platform)
        return
      }

      respondOnce(perm, e.name)
    })
    onCleanup(unsubscribe)

    // Backstop reconcile: the event listener above auto-responds to each
    // permission.asked, but a dropped/missed event (or a mode-resolution race
    // right after a mode switch) can leave a prompt sitting UNANSWERED in an
    // auto-accept mode — the reported "full/auto access still asks" hang. Re-check
    // the engine's PENDING permissions on a short interval and auto-respond to any
    // the active mode should accept (the same list+respondOnce the mode-switch
    // path already uses). respondOnce dedups, so this never double-replies; it
    // only runs while some auto-accept mode is active, so it's idle otherwise.
    const reconcilePending = () => {
      const dir = decode64(params.dir)
      if (!dir) return
      const anyAuto =
        Object.values(store.mode).some((m) => m === "full-access" || m === "auto-review") ||
        Object.values(store.autoAccept).some(Boolean)
      if (!anyAuto) return
      globalSDK.client.permission
        .list({ directory: dir })
        .then((x) => {
          for (const perm of x.data ?? []) {
            if (!perm?.id) continue
            if (!shouldAutoRespond(perm, dir)) continue
            respondOnce(perm, dir)
          }
        })
        .catch(() => undefined)
    }
    const reconcileInterval = setInterval(reconcilePending, 1500)
    onCleanup(() => clearInterval(reconcileInterval))

    function enableDirectory(directory: string) {
      const key = directoryAcceptKey(directory)
      setStore(
        produce((draft) => {
          draft.autoAccept[key] = true
          draft.mode[key] = "full-access"
        }),
      )

      globalSDK.client.permission
        .list({ directory })
        .then((x) => {
          if (!isAutoAcceptingDirectory(directory)) return
          for (const perm of x.data ?? []) {
            if (!perm?.id) continue
            if (!shouldAutoRespond(perm, directory)) continue
            respondOnce(perm, directory)
          }
        })
        .catch(() => undefined)
    }

    function disableDirectory(directory: string) {
      const key = directoryAcceptKey(directory)
      setStore(
        produce((draft) => {
          draft.autoAccept[key] = false
          draft.mode[key] = "default"
        }),
      )
    }

    function setDirectoryMode(mode: PermissionMode, directory: string) {
      const update = permissionModeStoreUpdate(mode, undefined, directory)
      setStore(
        produce((draft) => {
          Object.assign(draft.mode, update.mode)
          Object.assign(draft.autoAccept, update.autoAccept)
        }),
      )

      if (!permissionModeAutoAccepts(mode)) return
      globalSDK.client.permission
        .list({ directory })
        .then((x) => {
          if (!isAutoAcceptingDirectory(directory)) return
          for (const perm of x.data ?? []) {
            if (!perm?.id) continue
            if (!shouldAutoRespond(perm, directory)) continue
            respondOnce(perm, directory)
          }
        })
        .catch(() => undefined)
    }

    function setMode(mode: PermissionMode, sessionID: string, directory: string) {
      const key = acceptKey(sessionID, directory)
      const version = bumpEnableVersion(sessionID, directory)
      const update = permissionModeStoreUpdate(mode, sessionID, directory)
      setStore(
        produce((draft) => {
          Object.assign(draft.mode, update.mode)
          Object.assign(draft.autoAccept, update.autoAccept)
          delete draft.autoAccept[sessionID]
          delete draft.mode[sessionID]
        }),
      )

      globalSDK.client.session
        .update({ directory, sessionID, permission: sessionPermissionRulesForMode(mode) })
        .catch(() => undefined)

      if (!permissionModeAutoAccepts(mode)) return
      globalSDK.client.permission
        .list({ directory })
        .then((x) => {
          if (enableVersion.get(key) !== version) return
          if (!isAutoAccepting(sessionID, directory)) return
          for (const perm of x.data ?? []) {
            if (!perm?.id) continue
            if (!shouldAutoRespond(perm, directory)) continue
            respondOnce(perm, directory)
          }
        })
        .catch(() => undefined)
    }

    // The mode is otherwise only written to the engine on an EXPLICIT picker
    // change (setMode → session.update). A fresh session therefore has an EMPTY
    // session.permission, and the engine then falls through to the agent's
    // allow-all blanket and never prompts (Permission.mergeSessionMode treats an
    // empty ruleset as "no mode active"). This writes the currently-displayed
    // mode's ruleset once per session so "Default Ask" actually asks.
    const modeApplied = new Set<string>()
    function applySessionMode(sessionID: string, directory: string) {
      if (!sessionID || !directory) return
      const key = acceptKey(sessionID, directory)
      if (modeApplied.has(key)) return
      modeApplied.add(key)
      const session = globalSync.child(directory, { bootstrap: false })[0].session
      const mode = sessionPermissionMode(store.mode, store.autoAccept, session, sessionID, directory)
      globalSDK.client.session
        .update({ directory, sessionID, permission: sessionPermissionRulesForMode(mode) })
        .catch(() => undefined)
    }

    function enable(sessionID: string, directory: string) {
      setMode("full-access", sessionID, directory)
    }

    function disable(sessionID: string, directory?: string) {
      bumpEnableVersion(sessionID, directory)
      const key = directory ? acceptKey(sessionID, directory) : sessionID
      setStore(
        produce((draft) => {
          draft.autoAccept[key] = false
          draft.mode[key] = "default"
          if (!directory) return
          delete draft.autoAccept[sessionID]
          delete draft.mode[sessionID]
        }),
      )

      if (directory) {
        globalSDK.client.session
          .update({ directory, sessionID, permission: sessionPermissionRulesForMode("default") })
          .catch(() => undefined)
      }
    }

    return {
      ready,
      respond,
      autoResponds(permission: PermissionRequest, directory?: string) {
        return shouldAutoRespond(permission, directory)
      },
      permissionMode(sessionID: string, directory?: string) {
        const session = directory ? globalSync.child(directory, { bootstrap: false })[0].session : []
        return sessionPermissionMode(store.mode, store.autoAccept, session, sessionID, directory)
      },
      directoryPermissionMode(directory: string) {
        return getDirectoryPermissionMode(store.mode, store.autoAccept, directory)
      },
      isAutoAccepting,
      isAutoAcceptingDirectory,
      toggleAutoAccept(sessionID: string, directory: string) {
        if (isAutoAccepting(sessionID, directory)) {
          disable(sessionID, directory)
          return
        }

        enable(sessionID, directory)
      },
      toggleAutoAcceptDirectory(directory: string) {
        if (isAutoAcceptingDirectory(directory)) {
          disableDirectory(directory)
          return
        }
        enableDirectory(directory)
      },
      enableAutoAccept(sessionID: string, directory: string) {
        if (isAutoAccepting(sessionID, directory)) return
        enable(sessionID, directory)
      },
      disableAutoAccept(sessionID: string, directory?: string) {
        disable(sessionID, directory)
      },
      setPermissionMode(mode: PermissionMode, sessionID: string, directory: string) {
        setMode(mode, sessionID, directory)
      },
      setDirectoryPermissionMode(mode: PermissionMode, directory: string) {
        setDirectoryMode(mode, directory)
      },
      ensureSessionMode(sessionID: string, directory: string) {
        applySessionMode(sessionID, directory)
      },
      permissionsEnabled,
      isPermissionAllowAll(directory: string) {
        const [childStore] = globalSync.child(directory)
        const perm = childStore.config.permission
        return typeof perm === "string" && perm === "allow"
      },
    }
  },
})
